import type { WebsiteResponse, UpdateWebsiteRequest, WebsitePage } from '@troia-v3/types';

/**
 * Conteúdo do arquivo JSON exportado. Inclui `modelId` (para validar a
 * compatibilidade de modelo no import) e `name` (informativo). NÃO inclui
 * campos privados/de sistema (id, appId, companyId, createdAt, updatedAt,
 * publishedAt, deletedAt), nem `domains`/`status` (específicos do site), nem
 * `databaseIds` (referências de base específicas do tenant).
 */
export interface WebsiteExportFile {
  modelId: string;
  name: string;
  globalConfig: Record<string, unknown>;
  pages: WebsitePage[];
}

/**
 * Monta o payload de exportação a partir do site.
 */
export function buildExportPayload(website: WebsiteResponse): WebsiteExportFile {
  return {
    modelId: website.modelId,
    name: website.name,
    globalConfig: website.globalConfig,
    pages: website.pages,
  };
}

export type ParseImportResult =
  | { ok: true; data: UpdateWebsiteRequest }
  | { ok: false; error: string };

/**
 * Lê o JSON de import, valida que o modelo bate com o site de destino e monta o
 * payload de update aplicável. Aplica APENAS globalConfig e pages —
 * name, domains, status, databaseIds e modelId NÃO são alterados pelo import.
 */
export function parseImport(jsonText: string, target: WebsiteResponse): ParseImportResult {
  let parsed: unknown;
  try {
    parsed = JSON.parse(jsonText);
  } catch {
    return { ok: false, error: 'Arquivo JSON inválido.' };
  }

  if (typeof parsed !== 'object' || parsed === null) {
    return { ok: false, error: 'Arquivo de exportação inválido.' };
  }

  const obj = parsed as Record<string, unknown>;

  if (typeof obj.modelId !== 'string' || !obj.modelId) {
    return { ok: false, error: 'Arquivo de exportação inválido (modelo ausente).' };
  }

  if (obj.modelId !== target.modelId) {
    return {
      ok: false,
      error: `Este arquivo é do modelo "${obj.modelId}", mas o site é "${target.modelId}". Importação cancelada.`,
    };
  }

  const data: UpdateWebsiteRequest = {};
  if (typeof obj.globalConfig === 'object' && obj.globalConfig !== null && !Array.isArray(obj.globalConfig)) {
    data.globalConfig = obj.globalConfig as Record<string, unknown>;
  }
  if (Array.isArray(obj.pages)) {
    data.pages = obj.pages as WebsitePage[];
  }

  return { ok: true, data };
}

/** Slug seguro para nome de arquivo (`<slug>.json`). */
export function slugifyFilename(name: string): string {
  const slug = name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // remove marcas diacriticas combinantes
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return `${slug || 'website'}.json`;
}

/** Dispara o download de um objeto como arquivo JSON no navegador. */
export function downloadJson(filename: string, obj: unknown): void {
  const blob = new Blob([JSON.stringify(obj, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
